/**
 * logFeed — interleave public chat messages into the game event log.
 *
 * The game log (RecentActivitySidebar) renders EventRow[] end to end: filters,
 * windowed paging, scroll targeting, and snapshot selection all key off
 * EventRow fields. Rather than teach every stage a second row type, a chat
 * message is dressed AS an EventRow (`LogRow` = EventRow + `chat` marker):
 *
 *  - `ts`        = the message's createdAt (epoch ms; comparable with event
 *                  ts — both are server-side clocks).
 *  - `eventIndex`= the index of the LAST game event at/before the message —
 *                  the snapshot anchor used on snapshot-aware surfaces.
 *  - `kinds`     = [] so the kind filter (which enumerates real event kinds)
 *                  simply hides chat rows while a kind filter is active.
 *  - `relatedPlayerIds`/`playerId` = the author's slot, so the by-player
 *                  filter includes their chat alongside their moves.
 *
 * Pure and RN-free for unit testing.
 */
import type { ChatMessageResponse } from '@warcarp/api-types'
import type { EventRow } from './gameLog'

export interface ChatRowMeta {
  messageId: string
  /** Author's user id (the inline chat types in other players' lines only). */
  authorId: string
  /** Emoji reactions, shown as small chips on the row's right edge. */
  reactions: ChatMessageResponse['reactions']
  authorName: string
  content: string
}

/** An event-log row that may be a chat message in EventRow shape. */
export type LogRow = EventRow & { chat?: ChatRowMeta }

/**
 * Index of the last event at/before `tsMs` (binary search; events are
 * seq-ordered and server-timestamped). -1 when the message predates every
 * event (e.g. lobby chat) — callers clamp to 0.
 */
export function eventIndexAtTs(events: readonly { ts: number }[], tsMs: number): number {
  let lo = 0
  let hi = events.length - 1
  let ans = -1
  while (lo <= hi) {
    const mid = (lo + hi) >> 1
    if (events[mid]!.ts <= tsMs) {
      ans = mid
      lo = mid + 1
    } else {
      hi = mid - 1
    }
  }
  return ans
}

/**
 * Merge chat messages into a newest-first event-row list, sorted by ts
 * descending. On a timestamp tie the chat row sorts ABOVE the event (a
 * message reacts to the event, so it reads as the newer entry).
 */
export function mergeChatIntoLog(args: {
  /** groupEvents output — newest-first. */
  rowsNewestFirst: readonly EventRow[]
  /** Chat messages (any order; the API sends oldest-first). */
  messages: readonly ChatMessageResponse[]
  /** The raw event array the rows were built from (index == eventIndex space). */
  events: readonly { ts: number }[]
  /** userId → slot string (player colour identity), for the player filter. */
  slotByUserId: ReadonlyMap<string, string>
}): LogRow[] {
  const { rowsNewestFirst, messages, events, slotByUserId } = args
  if (messages.length === 0) return [...rowsNewestFirst]

  const chatRows: LogRow[] = messages.map((m) => {
    const ts = Date.parse(m.createdAt)
    const anchor = Math.max(eventIndexAtTs(events, ts), 0)
    const slot = slotByUserId.get(m.userId) ?? ''
    // Turn label: adopt the nearest row at/before the anchor (rows are
    // newest-first ⇒ first row with eventIndex <= anchor is the nearest).
    const anchorRow = rowsNewestFirst.find((r) => r.eventIndex <= anchor)
    return {
      key: `chat-${m.id}`,
      primary: `${m.displayName}: ${m.content}`,
      sub: [],
      kinds: [],
      turn: anchorRow?.turn ?? 0,
      ts,
      isTurnStart: false,
      isGameEnd: false,
      playerId: slot,
      relatedPlayerIds: slot ? [slot] : [],
      eventIndex: anchor,
      chat: { messageId: m.id, authorId: m.userId, authorName: m.displayName, content: m.content, reactions: m.reactions ?? [] },
    }
  })

  const merged: LogRow[] = [...rowsNewestFirst, ...chatRows]
  merged.sort((a, b) => b.ts - a.ts || (a.chat ? 0 : 1) - (b.chat ? 0 : 1))
  return merged
}
